// Google Form for SFI dashboard user feedback. Opened as a new tab on
// every platform — we tried embedding via iframe but Google Forms returns
// `X-Frame-Options: SAMEORIGIN` whenever the form has any kind of sign-in
// or domain restriction, which broke the embed mid-demo. New-tab is also
// kinder to mobile (Forms iframes are cramped on phones and lose state on
// rotate), so the cross-device behavior is consistent.
//
// Note: if the form requires Workspace sign-in, conference attendees on
// personal devices won't be able to submit it at all — make sure the
// Form's response settings allow anonymous public submission.
export const FEEDBACK_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSckhyAa1qJIZPNApX592LFwiu3o4SywJrKZNGPuotPngmrMPQ/viewform';

// Delay before the auto-prompt appears, measured from the moment the
// WelcomeModal closes (or page mount, on return visits where Welcome is
// suppressed). 150 s = 2.5 minutes — long enough for a conference attendee
// scanning the QR off the one-pager to actually explore a scenario or two
// before being interrupted with a feedback ask. Shorter values felt pushy
// during dry-runs; longer values risk the user closing the tab before the
// prompt fires.
export const FEEDBACK_PROMPT_DELAY_MS = 150_000;

// localStorage keys. Read defensively (try/catch) — Safari private mode
// throws on access. Fail-closed: if storage isn't available, treat the
// prompt as already-dismissed so we don't pester the user every page load.
const PROMPT_DISMISSED_KEY = 'sfi.feedbackPromptDismissed';

export const readFeedbackDismissed = (): boolean => {
  try {
    return localStorage.getItem(PROMPT_DISMISSED_KEY) === '1';
  } catch {
    return true;
  }
};

export const writeFeedbackDismissed = (): void => {
  try {
    localStorage.setItem(PROMPT_DISMISSED_KEY, '1');
  } catch {
    // ignore quota / privacy-mode failures
  }
};

// Force-show the auto-prompt via `?feedback=1` for QA + dry-runs without
// having to clear localStorage. Mirrors the `?welcome=1` pattern used by
// WelcomeContext.
export const isForceFeedbackPrompt = (): boolean => {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('feedback') === '1';
};

// Open the form in a new tab. Centralised so the trigger pill and the
// auto-prompt go through one path — easier to swap (e.g. to a Typeform
// or a hosted form) without hunting down call sites.
export const openFeedbackForm = (): void => {
  if (typeof window === 'undefined') return;
  window.open(FEEDBACK_FORM_URL, '_blank', 'noopener,noreferrer');
};
